import _ from "lodash";
import React, { useState } from "react";
import useI18n from "Hooks/UseI18n";

// components
import SkeletonLoader from "UIComponents/SkeletonLoader";

// design system
import {
  ExpandArrowsFourOutlined,
  EyeSlashOutlined,
  PlayFilled,
} from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";

// third party
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";

// utils

import { getThumbUrl } from "Utils";
import handleEnterKey from "Utils/handleEnterKey";
import { ATTACHMENT_TYPES } from "Utils/MessageHelpers";

// modules
import { updateAIData } from "ClassRoom/modules/ClassRoomModule";

import classes from "./MediaWrapper.scss";

const mapIconsToAttachmentType = {
  [ATTACHMENT_TYPES.IMAGE]: ExpandArrowsFourOutlined,
  [ATTACHMENT_TYPES.VIDEO]: PlayFilled,
};

export const MEDIA_VISIBILITY = {
  HIDDEN: "HIDDEN",
  ONLY_HOVER: "ONLY_HOVER",
  PERSISTENT: "PERSISTENT",
};

const MediaWrapper = props => {
  const {
    width,
    height,
    attachment: _attachment,
    onMediaClick,
    imgClassName,
    thumbnailResolution,
    overlayVisibility,
    iconVisibility,
    customIcon,
    showPlaceholder,
    containerStyle,
    updateAIData,
    showSensitiveContentIdsMapping,
  } = props;

  const { t } = useI18n();

  const attachment = {
    ...(_attachment || {}),
    url: _attachment?.signedUrl || _attachment?.url,
  };

  const hasExplicitContent = _.includes(
    _.keys(_.pickBy(showSensitiveContentIdsMapping, value => value)),
    attachment?.id
  );

  const { name, type } = attachment;

  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  const isMediaClickable = _.isFunction(onMediaClick) && !hasExplicitContent;

  const handleMediaLoad = () => setIsMediaLoaded(true);

  const containerStyleLocal = { width: `${width}px`, height: `${height}px` };

  const { thumbUrl: src } = getThumbUrl({
    attachment: attachment,
    width: thumbnailResolution.width || width,
    height: thumbnailResolution.height || height,
    fitIn: true,
  });

  const handleClick = e => {
    e.stopPropagation();
    if (isMediaClickable) onMediaClick(attachment);
  };

  const onClickSeePhoto = e => {
    e.stopPropagation();
    updateAIData({
      showSensitiveContentIdsMapping: {
        ...showSensitiveContentIdsMapping,
        [attachment.id]: false,
      },
    });
  };

  const Icon = mapIconsToAttachmentType[type];

  // by default it will be visible on hover
  const overlayClasses = classNames(classes.overlay, {
    [classes.persistentOverlay]:
      overlayVisibility === MEDIA_VISIBILITY.PERSISTENT,
  });

  // by default it will be visible on hover
  const iconContainerClasses = classNames(classes.iconContainer, {
    [classes.persistentIconContainer]:
      iconVisibility === MEDIA_VISIBILITY.PERSISTENT,
  });

  const isPlaceholderVisible = !isMediaLoaded && showPlaceholder;

  return (
    <div
      style={{ ...containerStyleLocal, ...containerStyle }}
      tabIndex={isMediaClickable ? 0 : null}
      onKeyDown={isMediaClickable ? handleEnterKey(handleClick) : null}
      role={isMediaClickable ? "button" : "presentation"}
      onClick={isMediaClickable ? handleClick : null}
      className={classes.container}
    >
      {overlayVisibility === MEDIA_VISIBILITY.HIDDEN ||
      hasExplicitContent ? null : (
        <div className={overlayClasses} />
      )}

      {overlayVisibility === MEDIA_VISIBILITY.HIDDEN ||
      hasExplicitContent ? null : (
        <div className={iconContainerClasses}>
          {customIcon ?? <Icon size="xx-small" variant="on" />}
        </div>
      )}

      {isPlaceholderVisible ? (
        <div className={classes.placeholderContainer}>
          <SkeletonLoader width="100%" height="100%" />
        </div>
      ) : null}

      {hasExplicitContent ? (
        <div className={classes.explicitContentOverlayContainer}>
          <div className={classes.explicitContentOverlay}>
            <div className={classes.explicitContentOverlayInfo}>
              <EyeSlashOutlined variant={"on"} />
              <div className={classes.explicitContentText}>
                <div className={"text-label"}>
                  {t("common:sensitive_content")}
                </div>
                <div className={"text-body-s"}>
                  {t("ai:this_photo_may_contain_sensitive_content")}
                </div>
              </div>
            </div>

            <Button
              variant={"neutral"}
              type={"fill"}
              dsVersion={"2.0"}
              size={"small"}
              onClick={onClickSeePhoto}
            >
              {t("common:see_label", { label: t("common:photo") })}
            </Button>
          </div>
        </div>
      ) : null}

      <img
        src={_.replace(src, "?v1", "")}
        onLoad={handleMediaLoad}
        className={classNames(classes.img, imgClassName)}
        loading="lazy"
        alt={
          type === ATTACHMENT_TYPES.VIDEO ? `preview for ${name} video` : name
        }
      />
    </div>
  );
};

MediaWrapper.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  attachment: PropTypes.object.isRequired,
  onMediaClick: PropTypes.func,
  imgClassName: PropTypes.string,
  overlayVisibility: PropTypes.oneOf(_.values(MEDIA_VISIBILITY)),
  iconVisibility: PropTypes.oneOf(_.values(MEDIA_VISIBILITY)),
  thumbnailResolution: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  customIcon: PropTypes.node,
  showPlaceholder: PropTypes.bool,
};

MediaWrapper.defaultProps = {
  onMediaClick: null,
  imgClassName: "",
  overlayVisibility: MEDIA_VISIBILITY.ONLY_HOVER,
  iconVisibility: MEDIA_VISIBILITY.ONLY_HOVER,
  thumbnailResolution: {},
  customIcon: null,
  showPlaceholder: true,
};

const mapStateToProps = state => {
  const showSensitiveContentIdsMapping = _.get(
    state,
    "classRoom.aiData.showSensitiveContentIdsMapping"
  );

  return { showSensitiveContentIdsMapping };
};

const mapActionCreators = {
  updateAIData,
};

export default connect(mapStateToProps, mapActionCreators)(MediaWrapper);
